/**
 * M7 S6 + S26: the re-apply list (plan §2 "The Career is the source of truth"; spec §1 rule 11, T3, T19).
 *
 * The worker holds only what it can re-derive from `career.story`. On load, on a fresh Reach (an import, a lost
 * companion) and after any story change, the story bridge (S26, `bridge.ts`) sends `reapplyCommands(career)`: an
 * ordered list of idempotent `set*` commands that rebuilds the worker's story state from the Career. The list is a
 * pure function of the Career (and the caller's world lookups), so sending it twice changes nothing and two calls
 * give equal lists.
 *
 * Order: the tier, the clock (S10's `storyMultOf`), the world flags (story flags, then Reyes's and Okafor's states),
 * Tethys's retaliation (and the live hunt's ship, keyed so it spawns once), the live floors and grievances (`floor`
 * countdowns), the Holdfast claims and Hale's charter waiver, the corporation's ballot for the finale path in force
 * (`bridge.json ballot`), the contested claim states (a voided Hale deal's charters and the Holdfast claims under
 * review; the corporation's claims harbouring on the Guardians path), then the restarted derelicts
 * (`restoreDerelict`) and their registrations (`registerReactivation` with `reapply: true`, so the worker moves no
 * money and no standing a second time; S20; with the record's S24 `marker` and `withFamily`, never its petty wait).
 *
 * **Never re-applied:** one-offs (`seedPressure`, `storyTierDrop`, transfers, bounties, Wire posts). The worker
 * doesn't record which tier drops fired, so `storyTierDrop` goes only from `syncClock`'s pending drop (S19 note).
 */
import { z } from 'zod'
import type { Career } from '../career/types'
import bridgeJson from '../data/story/bridge.json'
import { MEASURE_NAMES, type MeasureName } from '../strategic/types'
import { pathsInForce, storyMultOf } from './clock'
import { floorCommands } from './countdowns'
import { storyData } from './data'
import { activeRetaliationRows, HUNT_COUNTDOWN_ID, HUNT_SHIP_KEY } from './leverage'
import { m3m4Config } from './okafor'
import { storyOf } from './state'
import {
  BALLOT_STANCES,
  FINALE_PATHS,
  STORY_FLAGS,
  type BallotStance,
  type FinalePath,
  type ReactivationState,
  type StoryCommand,
  type StoryFinale,
  type StoryParty,
  type StoryState,
} from './types'

// ---------------------------------------------------------------------------------------------
// bridge.json
// ---------------------------------------------------------------------------------------------

const stancesSchema = z.partialRecord(z.enum(MEASURE_NAMES), z.enum(BALLOT_STANCES))

export const bridgeFileSchema = z.strictObject({
  comment: z.string(),
  ballot: z.partialRecord(z.enum(FINALE_PATHS), stancesSchema),
  harbouringGroup: z.string().min(1),
})
export type BridgeConfig = z.output<typeof bridgeFileSchema>

let cached: BridgeConfig | undefined

/** The shipped `bridge.json`, parsed once on first use (outside `storyData()`, like `hooks.json`). */
export function bridgeConfig(): BridgeConfig {
  if (cached === undefined) {
    const r = bridgeFileSchema.safeParse(bridgeJson)
    if (!r.success) {
      const issue = r.error.issues[0]
      const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
      throw new Error(`story data: bridge.json: ${where}: ${issue?.message ?? 'invalid'}`)
    }
    cached = r.data
  }
  return cached
}

// ---------------------------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------------------------

export interface ReapplyOptions {
  /** The clock derivation; defaults to S10's `storyMultOf` over the shipped `clock.json`. */
  storyMult?: (career: Career) => number
  /** Where the live hunt re-spawns (the player's system); without it the hunt ship is not re-sent. */
  systemId?: string
  /** POI id → system id (`reactivation/eligibility.ts poiSystemIndex`); without it no `restoreDerelict` is sent. */
  systemOfPoi?: (poiId: string) => string | undefined
  /** Tests pass their own. */
  bridge?: BridgeConfig
}

/** Restart states the worker holds a restored record for (lit or registered, not yet reverted). */
const RESTORED_STATES: ReadonlySet<ReactivationState> = new Set<ReactivationState>(['lit', 'title', 'handedOver', 'station', 'dark'])

/** The path whose ballot the corporation casts: Expose while it is in force, else the newest path in force. */
export function ballotPath(finale: StoryFinale): FinalePath | undefined {
  const paths = pathsInForce(finale)
  if (paths.includes('expose')) return 'expose'
  return paths[paths.length - 1]
}

/**
 * The corporation's ballot (spec §8.2): the default of the path whose ballot it casts, with the corporation screen's
 * toggle (S39, `finale.ballot`) over it. Nothing before the first commit.
 */
export function ballotStances(story: Pick<StoryState, 'finale'>, cfg: BridgeConfig = bridgeConfig()): Partial<Record<MeasureName, BallotStance>> | undefined {
  const path = ballotPath(story.finale)
  const base = path === undefined ? undefined : cfg.ballot[path]
  if (path === undefined) return undefined
  return story.finale.ballot === undefined ? base : { ...base, ...story.finale.ballot }
}

function ballotCommands(story: StoryState, cfg: BridgeConfig): StoryCommand[] {
  const stances = ballotStances(story, cfg)
  if (stances === undefined) return []
  const out: StoryCommand[] = []
  for (const measure of MEASURE_NAMES) {
    const stance = stances[measure]
    if (stance !== undefined) out.push({ type: 'setPlayerBallot', measure, stance })
  }
  return out
}

function claimStateCommands(story: StoryState, cfg: BridgeConfig): StoryCommand[] {
  const out: StoryCommand[] = []
  if (story.flags.hale_deal_voided === true && story.hale !== undefined) {
    for (const id of story.hale.charterIds ?? []) out.push({ type: 'setClaimState', scope: 'commission', id, state: 'underReview', untilOnlineDay: null })
    out.push({ type: 'setClaimState', scope: 'claim', id: m3m4Config().hale.holdfastClaimId, state: 'underReview', untilOnlineDay: null })
  }
  if (pathsInForce(story.finale).includes('guardians')) {
    out.push({ type: 'setClaimState', scope: 'claim', id: cfg.harbouringGroup, state: 'harbouring', untilOnlineDay: null })
  }
  return out
}

function byPoi(a: { poiId: string }, b: { poiId: string }): number {
  return a.poiId < b.poiId ? -1 : a.poiId > b.poiId ? 1 : 0
}

function reactivationCommands(story: StoryState, opts: ReapplyOptions): StoryCommand[] {
  const out: StoryCommand[] = []
  const live = story.reactivations.filter((r) => RESTORED_STATES.has(r.state)).sort(byPoi)
  const classes = storyData().reactivation.classes
  if (opts.systemOfPoi !== undefined) {
    for (const r of live) {
      const systemId = opts.systemOfPoi(r.poiId)
      if (systemId === undefined || r.frClass === undefined) continue
      const owner: StoryParty = r.registrant === 'handover' && r.recipient !== undefined ? r.recipient : 'player'
      out.push({ type: 'restoreDerelict', poiId: r.poiId, systemId, template: classes[r.frClass].template, owner })
    }
  }
  for (const r of live) {
    if (r.registrant === undefined) continue
    const cmd: StoryCommand = { type: 'registerReactivation', poiId: r.poiId, registrant: r.registrant, fee: 0, reapply: true }
    if (r.recipient !== undefined) cmd.recipient = r.recipient
    // S24: a fresh Reach keeps the marker and the family share (the worker restores the flags, moves no standing).
    if (r.marker === true) cmd.marker = true
    if (r.withFamily === true) cmd.withFamily = true
    out.push(cmd)
  }
  return out
}

/** The idempotent worker command list that rebuilds the story's worker state from the Career. */
export function reapplyCommands(career: Career, opts: ReapplyOptions = {}): StoryCommand[] {
  const story = storyOf(career)
  const cfg = opts.bridge ?? bridgeConfig()
  const out: StoryCommand[] = []
  out.push({ type: 'setStoryTier', tier: career.storyTier ?? 0 })
  out.push({ type: 'setStoryClock', storyMult: (opts.storyMult ?? storyMultOf)(career) })

  for (const flag of STORY_FLAGS) if (story.flags[flag] === true) out.push({ type: 'setWorldFlag', key: flag, value: true })
  if (story.reyesState !== undefined) out.push({ type: 'setWorldFlag', key: 'reyes_state', value: story.reyesState })
  if (story.okaforState !== undefined) out.push({ type: 'setWorldFlag', key: 'okafor_state', value: story.okaforState })

  if (story.retaliation !== undefined) {
    out.push({ type: 'setRetaliation', faction: 'tethys', rows: activeRetaliationRows(story) })
    const hunt = story.countdowns.find((c) => c.id === HUNT_COUNTDOWN_ID)
    if (hunt !== undefined && hunt.dueOnlineDay > story.onlineDays && opts.systemId !== undefined) {
      out.push({ type: 'spawnStoryShip', key: HUNT_SHIP_KEY, role: 'assetRecovery', systemId: opts.systemId })
    }
  }

  out.push(...floorCommands(story))

  if (story.flags.hale_deal === true) out.push({ type: 'registerHoldfastClaims', registered: true })
  if (story.hale !== undefined) {
    const voided = story.flags.hale_deal_voided === true
    out.push({ type: 'setCharterWaiver', charters: voided ? 0 : story.hale.chartersLeft, waiveMinStanding: !voided })
  }

  out.push(...ballotCommands(story, cfg))
  out.push(...claimStateCommands(story, cfg))
  out.push(...reactivationCommands(story, opts))
  return out
}
